import { useEffect, useState } from "react";
import {
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
  AlertCircle,
  MinusCircle,
  Timer,
  ExternalLink,
  GitBranch,
  Hash,
} from "lucide-react";
import { buildsApi } from "../api/client";
import { format, formatDistanceToNow } from "date-fns";

const STATUS = {
  success: {
    Icon: CheckCircle2,
    cls: "text-green-600",
    bg: "bg-green-50",
    border: "border-green-200",
    rowBorder: "border-l-green-500",
    label: "Success",
  },
  running: {
    Icon: Loader2,
    cls: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    rowBorder: "border-l-blue-500",
    label: "Running",
    spin: true,
  },
  failed: {
    Icon: AlertCircle,
    cls: "text-red-600",
    bg: "bg-red-50",
    border: "border-red-200",
    rowBorder: "border-l-red-500",
    label: "Failed",
  },
  cancelled: {
    Icon: MinusCircle,
    cls: "text-slate-500",
    bg: "bg-slate-100",
    border: "border-slate-200",
    rowBorder: "border-l-slate-300",
    label: "Cancelled",
  },
  queued: {
    Icon: Clock,
    cls: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-200",
    rowBorder: "border-l-amber-400",
    label: "Queued",
  },
  timeout: {
    Icon: Timer,
    cls: "text-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-200",
    rowBorder: "border-l-orange-500",
    label: "Timeout",
  },
  expired: {
    Icon: XCircle,
    cls: "text-slate-400",
    bg: "bg-slate-50",
    border: "border-slate-200",
    rowBorder: "border-l-slate-200",
    label: "Expired",
  },
};

const TABS = [
  { value: "all", label: "All" },
  { value: "running", label: "Running" },
  { value: "queued", label: "Queued" },
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "timeout", label: "Timeout" },
];

function formatDuration(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatCreated(iso) {
  if (!iso) return { display: "—", title: "" };
  const d = new Date(iso);
  return {
    display: format(d, "MMM d, h:mm a"),
    title: formatDistanceToNow(d, { addSuffix: true }),
  };
}

function StatusBadge({ status }) {
  const sc = STATUS[status] || STATUS.queued;
  const { Icon } = sc;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${sc.bg} ${sc.border} ${sc.cls}`}
    >
      <Icon size={11} className={sc.spin ? "animate-spin" : ""} />
      {sc.label}
    </span>
  );
}

function RegionBadge({ region }) {
  if (!region) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600 border border-slate-200 font-mono whitespace-nowrap">
      {region}
    </span>
  );
}

const PAGE_SIZE_OPTIONS = [10, 30, 50, 100, 150, 200];

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const delta = 2;
  const left = Math.max(2, page - delta);
  const right = Math.min(totalPages - 1, page + delta);

  pages.push(1);
  if (left > 2) pages.push("…");
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < totalPages - 1) pages.push("…");
  if (totalPages > 1) pages.push(totalPages);

  return (
    <div className="flex items-center justify-center gap-1 pt-1">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className="px-3 py-1.5 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        ← Prev
      </button>
      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`ellipsis-${i}`} className="px-2 text-xs text-slate-400">
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`w-8 h-8 text-xs rounded border transition-colors ${
              p === page
                ? "bg-blue-600 border-blue-600 text-white font-semibold"
                : "border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {p}
          </button>
        ),
      )}
      <button
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        className="px-3 py-1.5 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Next →
      </button>
    </div>
  );
}

export default function Builds() {
  const [builds, setBuilds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  function fetchBuilds(isRefresh = false, limit = pageSize) {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    setSelected(new Set());
    setPage(1);
    buildsApi
      .list({ limit })
      .then((data) => setBuilds(data || []))
      .catch((e) => setError(e.error || e.message || "Failed to load builds"))
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }

  // Re-fetch from the server whenever pageSize changes
  useEffect(() => {
    fetchBuilds(false, pageSize);
  }, [pageSize]);

  // Auto-refresh every 30s while any build is running or queued.
  // Silent update — does not reset page or selected state.
  useEffect(() => {
    const hasActive = builds.some(b => b.status === 'running' || b.status === 'queued');
    if (!hasActive) return;
    const limit = pageSize;
    const id = setInterval(() => {
      buildsApi.list({ limit }).then(data => setBuilds(data || [])).catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, [builds, pageSize]);

  const filtered = builds.filter((b) => tab === "all" || b.status === tab);
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  const allChecked =
    paginated.length > 0 && paginated.every((b) => selected.has(b.id));

  function changeTab(value) {
    setTab(value);
    setPage(1);
    setSelected(new Set());
  }

  function toggleAll() {
    setSelected(allChecked ? new Set() : new Set(paginated.map((b) => b.id)));
  }
  function toggleOne(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 size={24} className="text-blue-500 animate-spin" />
        <p className="text-sm text-slate-500">Loading build history…</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">
              Build history
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {builds.length > 0
                ? `${builds.length} builds, sorted by most recent`
                : "Builds sorted by most recently started"}
            </p>
          </div>
          <button
            onClick={() => fetchBuilds(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 transition-colors shadow-sm"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      <div className="px-6 py-4 space-y-4">
        {error && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <AlertCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {!error && (
          <>
            {/* Tabs */}
            <div className="flex items-center border-b border-slate-200 gap-1">
              {TABS.map((t) => {
                const count =
                  t.value === "all"
                    ? builds.length
                    : builds.filter((b) => b.status === t.value).length;
                if (t.value !== "all" && count === 0) return null;
                const active = tab === t.value;
                return (
                  <button
                    key={t.value}
                    onClick={() => changeTab(t.value)}
                    className={`flex items-center gap-1.5 px-3 py-2.5 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${
                      active
                        ? "border-blue-600 text-blue-600 font-medium"
                        : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
                    }`}
                  >
                    {t.label}
                    <span
                      className={`text-xs rounded-full px-1.5 py-0.5 font-medium ${
                        active
                          ? "bg-blue-100 text-blue-700"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Empty */}
            {filtered.length === 0 && (
              <div className="flex flex-col items-center gap-3 py-20 text-center">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                  <Clock size={20} className="text-slate-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    No builds found
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {tab === "all"
                      ? "No builds have run yet."
                      : `No ${tab} builds.`}
                  </p>
                </div>
              </div>
            )}

            {/* Table */}
            {filtered.length > 0 && (
              <>
                <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                  {selected.size > 0 && (
                    <div className="flex items-center gap-3 px-4 py-2.5 bg-blue-50 border-b border-blue-100 text-sm text-blue-700">
                      <span className="font-medium">
                        {selected.size} selected
                      </span>
                      <button
                        onClick={() => setSelected(new Set())}
                        className="text-blue-500 hover:text-blue-700 underline"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="w-1 p-0" />
                          <th className="w-10 px-4 py-3">
                            <input
                              type="checkbox"
                              checked={allChecked}
                              onChange={toggleAll}
                              className="rounded border-slate-300 text-blue-600 cursor-pointer"
                            />
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                            Status
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                            Build ID
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Region
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Source
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Ref
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Commit
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                            Trigger
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Duration
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {paginated.map((build) => {
                          const sc = STATUS[build.status] || STATUS.queued;
                          const created = formatCreated(
                            build.startTime || build.createTime,
                          );
                          const isSelected = selected.has(build.id);
                          return (
                            <tr
                              key={build.id}
                              className={`transition-colors ${isSelected ? "bg-blue-50/60" : "hover:bg-slate-50/80"}`}
                            >
                              {/* Status border */}
                              <td
                                className={`w-1 p-0 border-l-4 ${sc.rowBorder}`}
                              />

                              {/* Checkbox */}
                              <td className="px-4 py-3.5">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleOne(build.id)}
                                  className="rounded border-slate-300 text-blue-600 cursor-pointer"
                                />
                              </td>

                              {/* Status badge */}
                              <td className="px-3 py-3.5 whitespace-nowrap">
                                <StatusBadge status={build.status} />
                              </td>

                              {/* Build ID */}
                              <td className="px-3 py-3.5 whitespace-nowrap">
                                <a
                                  href={build.logUrl || "#"}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={build.id}
                                  className="inline-flex items-center gap-1 font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline bg-blue-50 px-2 py-1 rounded border border-blue-100"
                                >
                                  {build.id?.slice(0, 8)}
                                  <ExternalLink
                                    size={10}
                                    className="text-blue-400 shrink-0"
                                  />
                                </a>
                              </td>

                              {/* Region */}
                              <td className="px-3 py-3.5">
                                <RegionBadge region={build.region} />
                              </td>

                              {/* Source */}
                              <td className="px-3 py-3.5 whitespace-nowrap">
                                {build.source ? (
                                  <a
                                    href={
                                      build.sourceUrl ||
                                      `https://github.com/${build.source}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                  >
                                    {build.source}
                                    <ExternalLink
                                      size={10}
                                      className="text-blue-400 shrink-0"
                                    />
                                  </a>
                                ) : (
                                  <span className="text-slate-400 text-xs">
                                    —
                                  </span>
                                )}
                              </td>

                              {/* Ref */}
                              <td className="px-3 py-3.5 max-w-[140px]">
                                {build.branch ? (
                                  <span className="flex items-center gap-1 text-xs text-slate-600">
                                    <GitBranch
                                      size={11}
                                      className="text-slate-400 shrink-0"
                                    />
                                    <span
                                      className="truncate"
                                      title={build.branch}
                                    >
                                      {build.branch}
                                    </span>
                                  </span>
                                ) : (
                                  <span className="text-slate-400 text-xs">
                                    —
                                  </span>
                                )}
                              </td>

                              {/* Commit */}
                              <td className="px-3 py-3.5 whitespace-nowrap">
                                {build.commit ? (
                                  <a
                                    href={build.commitUrl || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline font-mono"
                                  >
                                    <Hash
                                      size={10}
                                      className="text-slate-400"
                                    />
                                    {build.commit.slice(0, 7)}
                                    <ExternalLink
                                      size={10}
                                      className="text-blue-400 shrink-0"
                                    />
                                  </a>
                                ) : (
                                  <span className="text-slate-400 text-xs">
                                    —
                                  </span>
                                )}
                              </td>

                              {/* Trigger */}
                              <td className="px-3 py-3.5 max-w-[180px]">
                                {build.triggerName ? (
                                  <span
                                    className="inline-block text-xs font-mono text-slate-700 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 truncate max-w-full"
                                    title={build.triggerName}
                                  >
                                    {build.triggerName}
                                  </span>
                                ) : (
                                  <span className="text-slate-400 text-xs">
                                    {build.triggeredBy || "—"}
                                  </span>
                                )}
                              </td>

                              {/* Created */}
                              <td className="px-3 py-3.5 whitespace-nowrap">
                                <span
                                  className="text-xs text-slate-600"
                                  title={created.title}
                                >
                                  {created.display}
                                </span>
                              </td>

                              {/* Duration */}
                              <td className="px-3 py-3.5 whitespace-nowrap">
                                {build.status === "running" ? (
                                  <span className="flex items-center gap-1 text-xs text-blue-500">
                                    <Loader2 size={11} className="animate-spin" />
                                    {formatDuration(
                                      build.startTime
                                        ? Math.round((now - new Date(build.startTime)) / 1000)
                                        : null
                                    )}
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1 text-xs text-slate-600">
                                    <Timer size={11} className="text-slate-400" />
                                    {formatDuration(build.duration)}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 bg-slate-50">
                    <span className="text-xs text-slate-400">
                      {filtered.length === builds.length
                        ? `${filtered.length} builds`
                        : `${filtered.length} of ${builds.length} builds`}
                      {totalPages > 1 && ` · page ${page} of ${totalPages}`}
                    </span>
                    <label className="flex items-center gap-2 text-xs text-slate-500">
                      Rows per page:
                      <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="border border-slate-200 rounded px-2 py-1 text-xs text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onChange={setPage}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
